import FileSaver from 'file-saver';
import Message from '../../../models/Message';

/**
 * Media information interface
 * Matches server-side MediaInfo in messageModel.v3.ts
 */
export interface MediaInfo {
  url: string;
  mimeType?: string;
  filename?: string;
  extension?: string;
  duration?: string; // For audio/video (format: "MM:SS")
  size?: number;
  fileRecordId?: string; // Reference to FileRecord document ID
}

/**
 * Extended Message type that includes the new media field
 * Used for type safety when working with new message format
 */
export interface MessageWithMedia extends Message {
  media?: MediaInfo;
}

/**
 * Download media from a message
 * Supports multiple signatures for backwards compatibility:
 * - saveMessageMedia(message) - new pattern with Message object
 * - saveMessageMedia(url, filename) - legacy pattern with separate args
 */
export function saveMessageMedia(message: Message | MessageWithMedia): void;
export function saveMessageMedia(mediaUrl: string | undefined, filename: string | undefined): void;
export function saveMessageMedia(
  messageOrUrl: Message | MessageWithMedia | string | undefined,
  filename?: string | undefined
): void {
  let url: string | undefined;
  let name: string;

  // Check if first argument is a Message object or a URL string
  if (typeof messageOrUrl === 'object' && messageOrUrl !== null) {
    // New pattern: saveMessageMedia(message)
    const message = messageOrUrl as MessageWithMedia;
    url = message.media?.url || message.mediaUrl;
    name = getFilenameWithExtension(message);
  } else {
    // Legacy pattern: saveMessageMedia(url, filename)
    url = messageOrUrl as string | undefined;
    name = filename || 'download';

    // Try to add extension from URL if filename doesn't have one
    if (url && name && !name.includes('.')) {
      const urlExtension = extractExtensionFromUrl(url);
      if (urlExtension) {
        name = `${name}${urlExtension}`;
      }
    }
  }

  if (!url) {
    alert("Invalid URL Please Retry Download");
    return;
  }

  FileSaver.saveAs(url, name);
}

/**
 * Get media URL from message (handles both patterns)
 */
export const getMediaUrl = (message: Message | MessageWithMedia): string | undefined => {
  const messageWithMedia = message as MessageWithMedia;
  return messageWithMedia.media?.url || message.mediaUrl;
};

/**
 * Get media filename from message (handles both patterns)
 */
export const getMediaFilename = (message: Message | MessageWithMedia): string | undefined => {
  const messageWithMedia = message as MessageWithMedia;
  return messageWithMedia.media?.filename || message.filename;
};

/**
 * Get media mime type from message
 * Only available in new format with media object
 */
export const getMediaMimeType = (message: Message | MessageWithMedia): string | undefined => {
  const messageWithMedia = message as MessageWithMedia;
  return messageWithMedia.media?.mimeType;
};

/**
 * Get media extension from message
 * Only available in new format with media object
 */
export const getMediaExtension = (message: Message | MessageWithMedia): string | undefined => {
  const messageWithMedia = message as MessageWithMedia;
  return messageWithMedia.media?.extension;
};

/**
 * Get media duration from message (for audio/video)
 * Supports both new media object and legacy duration field
 */
export const getMediaDuration = (message: Message | MessageWithMedia): string | undefined => {
  const messageWithMedia = message as MessageWithMedia;
  return messageWithMedia.media?.duration || message.duration;
};

/**
 * Check if message has media content
 */
export const hasMedia = (message: Message | MessageWithMedia): boolean => {
  return !!(getMediaUrl(message));
};

/**
 * Map MIME types to file extensions
 */
const mimeTypeToExtension: Record<string, string> = {
  // Images
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/svg+xml': '.svg',
  'image/bmp': '.bmp',
  'image/tiff': '.tiff',
  'image/x-icon': '.ico',

  // Documents
  'application/pdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'text/plain': '.txt',
  'text/csv': '.csv',
  'text/html': '.html',
  'application/rtf': '.rtf',

  // Archives
  'application/zip': '.zip',
  'application/x-rar-compressed': '.rar',
  'application/x-7z-compressed': '.7z',
  'application/x-tar': '.tar',
  'application/gzip': '.gz',

  // Video
  'video/mp4': '.mp4',
  'video/mpeg': '.mpeg',
  'video/quicktime': '.mov',
  'video/x-msvideo': '.avi',
  'video/x-matroska': '.mkv',
  'video/webm': '.webm',

  // Audio
  'audio/mpeg': '.mp3',
  'audio/mp4': '.m4a',
  'audio/ogg': '.ogg',
  'audio/wav': '.wav',
  'audio/webm': '.weba',
  'audio/aac': '.aac',

  // Other
  'application/json': '.json',
  'application/xml': '.xml',
  'application/octet-stream': '.bin',
};

/**
 * Get extension from MIME type
 * Returns extension with dot (e.g., ".jpg", ".pdf")
 */
const getExtensionFromMimeType = (mimeType: string): string | null => {
  const normalized = mimeType.toLowerCase().split(';')[0].trim();
  return mimeTypeToExtension[normalized] || null;
};

/**
 * Extract extension from URL
 * Returns extension with dot (e.g., ".jpg", ".pdf")
 */
const extractExtensionFromUrl = (url: string): string | null => {
  try {
    // Remove query parameters and hash
    const cleanUrl = url.split('?')[0].split('#')[0];
    // Get the last part after the last dot
    const lastDotIndex = cleanUrl.lastIndexOf('.');
    const lastSlashIndex = cleanUrl.lastIndexOf('/');

    // Make sure the dot comes after the last slash (it's part of filename, not directory)
    if (lastDotIndex > lastSlashIndex && lastDotIndex !== -1) {
      const ext = cleanUrl.substring(lastDotIndex);
      // Validate it looks like an extension (reasonable length)
      if (ext.length <= 6 && ext.length > 1) {
        return ext;
      }
    }
  } catch (e) {
    // Ignore parsing errors
  }
  return null;
};

/**
 * Ensure filename has proper extension
 * Filename priority: filename > fileRecordId > "Untitled"
 * Extension priority: existing extension > media.extension > mimeType > URL extraction
 */
export const getFilenameWithExtension = (message: Message | MessageWithMedia): string => {
  const messageWithMedia = message as MessageWithMedia;

  // Priority: filename > fileRecordId > "Untitled"
  const filename = messageWithMedia.media?.filename
    || message.filename
    || messageWithMedia.media?.fileRecordId
    || message.fileId
    || 'Untitled';

  const extension = messageWithMedia.media?.extension;
  const mimeType = messageWithMedia.media?.mimeType;
  const url = messageWithMedia.media?.url || message.mediaUrl;

  // Check if filename already has an extension
  const hasExtension = filename.includes('.') && filename.lastIndexOf('.') > filename.length - 6;

  if (hasExtension) {
    return filename;
  }

  // Priority 1: Try to use media.extension first
  if (extension) {
    const ext = extension.startsWith('.') ? extension : `.${extension}`;
    return `${filename}${ext}`;
  }

  // Priority 2: Try to get extension from mimeType
  if (mimeType) {
    const mimeExtension = getExtensionFromMimeType(mimeType);
    if (mimeExtension) {
      return `${filename}${mimeExtension}`;
    }
  }

  // Priority 3: Try to extract extension from URL
  if (url) {
    const urlExtension = extractExtensionFromUrl(url);
    if (urlExtension) {
      return `${filename}${urlExtension}`;
    }
  }

  // Return filename as-is if no extension found
  return filename;
};
